import React from 'react';
import { MapPinIcon, TruckIcon, UserIcon } from '@heroicons/react/24/outline';
import type { EventTravel, TravelPassenger, TravelVehicle } from '../../types/travel';
import { formatTravelTime } from '../../types/travel';

interface TravelPlanViewProps {
  travel: EventTravel;
  /** A beosztatlan plusz személyek csak a szervezőnek érdekesek. */
  showUnassigned?: boolean;
  /** A saját gyerek(ek) összefoglalója elhagyható, ha a jelzőpanel már mutatja. */
  hideMine?: boolean;
  /** Az úti cél és az "utoljára módosította" sor elhagyható (a családi nézetben ott az esemény adatai). */
  compact?: boolean;
}

const sameDay = (a: string, b: string) => new Date(a).toDateString() === new Date(b).toDateString();

const PassengerChip: React.FC<{ passenger: TravelPassenger }> = ({ passenger }) => (
  <li
    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
      passenger.isMine ? 'bg-primary-100 font-semibold text-primary-800' : 'bg-gray-100 text-gray-700'
    }`}
    title={passenger.notes ?? undefined}
  >
    {passenger.name}
    {passenger.role && <span className="font-normal text-gray-500">({passenger.role})</span>}
  </li>
);

const VehicleCard: React.FC<{ vehicle: TravelVehicle; passengers: TravelPassenger[]; eventStart: string }> = ({
  vehicle,
  passengers,
  eventStart,
}) => {
  const aboard = passengers.filter((p) => p.travelVehicleId === vehicle.id);
  const withoutStop = aboard.filter((p) => !p.travelStopId || !vehicle.stops.some((s) => s.id === p.travelStopId));
  const full = aboard.length >= vehicle.seats;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <TruckIcon className="h-5 w-5 shrink-0 text-gray-500" />
            <span className="font-semibold text-gray-900">{vehicle.name}</span>
            {vehicle.isOffered && (
              <span className="rounded bg-sky-50 px-1.5 py-0.5 text-[11px] font-medium text-sky-800 ring-1 ring-sky-200">
                {vehicle.isMine ? 'Az autód' : 'Szülő ajánlotta fel'}
              </span>
            )}
          </div>
          {vehicle.driverName && <p className="mt-0.5 text-xs text-gray-500">Sofőr: {vehicle.driverName}</p>}
          {vehicle.notes && <p className="mt-0.5 text-xs text-gray-500">{vehicle.notes}</p>}
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
            full ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
          }`}
        >
          {aboard.length} / {vehicle.seats} hely
        </span>
      </div>

      {vehicle.stops.length > 0 && (
        <ol className="mt-3 space-y-2 border-l-2 border-gray-200 pl-3">
          {vehicle.stops.map((stop) => {
            const here = aboard.filter((p) => p.travelStopId === stop.id);
            return (
              <li key={stop.id}>
                <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
                  {stop.meetTime && (
                    <span className="font-semibold text-gray-900">
                      {formatTravelTime(stop.meetTime, !sameDay(stop.meetTime, eventStart))}
                    </span>
                  )}
                  <span className="font-medium text-gray-800">{stop.name}</span>
                  {stop.address && <span className="text-xs text-gray-500">{stop.address}</span>}
                </div>
                {here.length > 0 && (
                  <ul className="mt-1 flex flex-wrap gap-1">
                    {here.map((p) => (
                      <PassengerChip key={p.id} passenger={p} />
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {withoutStop.length > 0 && (
        <div className="mt-3">
          {vehicle.stops.length > 0 && <p className="mb-1 text-xs text-gray-500">Megálló nélkül:</p>}
          <ul className="flex flex-wrap gap-1">
            {withoutStop.map((p) => (
              <PassengerChip key={p.id} passenger={p} />
            ))}
          </ul>
        </div>
      )}

      {aboard.length === 0 && <p className="mt-2 text-xs text-gray-400">Még nincs beosztva senki.</p>}
    </div>
  );
};

/** Az utazás csak olvasható nézete: járművek a megállóikkal és utasaikkal. */
const TravelPlanView: React.FC<TravelPlanViewProps> = ({ travel, showUnassigned = false, hideMine = false, compact = false }) => {
  const direct = travel.passengers.filter((p) => p.goesDirectly);
  // A fuvarra váró játékosokat a csapat is látja, hogy aki tud, felvehesse őket
  const needsRide = travel.passengers.filter((p) => p.playerId && !p.goesDirectly && !p.travelVehicleId);
  const unassignedExtras = travel.passengers.filter((p) => !p.playerId && !p.goesDirectly && !p.travelVehicleId);
  const mine = hideMine ? [] : travel.passengers.filter((p) => p.isMine);

  return (
    <div className="space-y-3">
      {mine.length > 0 && (
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-3 text-sm">
          {mine.map((p) => {
            const vehicle = travel.vehicles.find((v) => v.id === p.travelVehicleId);
            const stop = vehicle?.stops.find((s) => s.id === p.travelStopId);
            return (
              <p key={p.id} className="text-primary-900">
                <span className="font-semibold">{p.name}:</span>{' '}
                {p.goesDirectly
                  ? 'egyenesen a helyszínre megy'
                  : vehicle
                    ? `${vehicle.name}${stop ? `, ${stop.name}${stop.meetTime ? `, ${formatTravelTime(stop.meetTime, !sameDay(stop.meetTime, travel.eventStart))}-ra` : ''}` : ''}`
                    : 'fuvarra vár'}
              </p>
            );
          })}
        </div>
      )}

      {!compact && travel.destination && (
        <p className="flex items-center gap-2 text-sm text-gray-600">
          <MapPinIcon className="h-4 w-4 shrink-0" />
          Úti cél: {travel.destination}
        </p>
      )}

      {travel.notes && <p className="whitespace-pre-line text-sm text-gray-700">{travel.notes}</p>}

      {travel.vehicles.length === 0 ? (
        <p className="text-sm text-gray-500">Még nincs jármű az utazáshoz.</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {travel.vehicles.map((vehicle) => (
            <VehicleCard
              key={vehicle.id}
              vehicle={vehicle}
              passengers={travel.passengers}
              eventStart={travel.eventStart}
            />
          ))}
        </div>
      )}

      {direct.length > 0 && (
        <div>
          <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
            <UserIcon className="h-4 w-4" />
            Egyenesen a helyszínre ({direct.length})
          </p>
          <ul className="flex flex-wrap gap-1">
            {direct.map((p) => (
              <PassengerChip key={p.id} passenger={p} />
            ))}
          </ul>
        </div>
      )}

      {needsRide.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium text-amber-700">Fuvarra vár ({needsRide.length})</p>
          <ul className="flex flex-wrap gap-1">
            {needsRide.map((p) => (
              <PassengerChip key={p.id} passenger={p} />
            ))}
          </ul>
        </div>
      )}

      {showUnassigned && unassignedExtras.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium text-amber-700">Beosztásra vár ({unassignedExtras.length})</p>
          <ul className="flex flex-wrap gap-1">
            {unassignedExtras.map((p) => (
              <PassengerChip key={p.id} passenger={p} />
            ))}
          </ul>
        </div>
      )}

      {!compact && travel.updatedByName && travel.updatedAt && (
        <p className="text-xs text-gray-400">
          Utoljára módosította: {travel.updatedByName},{' '}
          {new Date(travel.updatedAt).toLocaleString('hu-HU', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      )}
    </div>
  );
};

export default TravelPlanView;
